import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@shared/services/apiClient";
import type {
  EnumRegistryData,
  EnumRegistryPriority,
  EnumRegistryResponse,
  EnumRegistryRole,
  EnumRegistryWorkflowStatus,
} from "@shared/contracts/enumRegistry";

const REGISTRY_CACHE_TTL_MS = 5 * 60 * 1000;

const EMPTY_REGISTRY: EnumRegistryData = {
  roles: [],
  priorities: [],
  workflowStatuses: [],
};

let cachedRegistry: EnumRegistryData | null = null;
let cacheExpiresAtMs = 0;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object");
}

function normalizeRoles(input: unknown): EnumRegistryRole[] {
  if (!Array.isArray(input)) return [];

  return input
    .filter(isRecord)
    .map((role) => ({
      code: String(role.code || "").trim().toUpperCase(),
      description:
        typeof role.description === "string" ? role.description : null,
      isSystem: Boolean(role.isSystem),
      isActive: Boolean(role.isActive),
      capabilities: isRecord(role.capabilities)
        ? {
            patientAccessScope: String(
              role.capabilities.patientAccessScope || "none",
            ),
            patientSensitivityTier: Number(
              role.capabilities.patientSensitivityTier || 0,
            ),
            canBypassConsentRestriction: Boolean(
              role.capabilities.canBypassConsentRestriction,
            ),
            canManageTempPatients: Boolean(
              role.capabilities.canManageTempPatients,
            ),
            mfaRequired: Boolean(role.capabilities.mfaRequired),
            requiresDeidentification: Boolean(
              role.capabilities.requiresDeidentification,
            ),
            canAccessTelehealth: Boolean(role.capabilities.canAccessTelehealth),
            isPrivilegedLogin: Boolean(role.capabilities.isPrivilegedLogin),
            canManageOrgAppointments: Boolean(
              role.capabilities.canManageOrgAppointments,
            ),
            canAccessMedicalCoding: Boolean(
              role.capabilities.canAccessMedicalCoding,
            ),
            canManageOrgEegConfig: Boolean(
              role.capabilities.canManageOrgEegConfig,
            ),
          }
        : {
            patientAccessScope: "none",
            patientSensitivityTier: 0,
            canBypassConsentRestriction: false,
            canManageTempPatients: false,
            mfaRequired: false,
            requiresDeidentification: false,
            canAccessTelehealth: false,
            isPrivilegedLogin: false,
            canManageOrgAppointments: false,
            canAccessMedicalCoding: false,
            canManageOrgEegConfig: false,
          },
    }))
    .filter((role) => Boolean(role.code));
}

function normalizePriorities(input: unknown): EnumRegistryPriority[] {
  if (!Array.isArray(input)) return [];

  return input
    .filter(isRecord)
    .map((item) => ({
      code: String(item.code || "").trim().toLowerCase(),
      name: String(item.name || "").trim(),
      color: String(item.color || "").trim(),
      order: Number(item.order || 0),
    }))
    .filter((item) => Boolean(item.code && item.name && item.color));
}

function normalizeWorkflowStatuses(input: unknown): EnumRegistryWorkflowStatus[] {
  if (!Array.isArray(input)) return [];

  return input
    .filter(isRecord)
    .map((item) => ({
      code: String(item.code || "").trim().toLowerCase(),
      name: String(item.name || "").trim(),
      color: String(item.color || "").trim(),
      order: Number(item.order || 0),
      domain: String(item.domain || "eeg_study").trim().toLowerCase(),
      isTerminal: Boolean(item.isTerminal),
    }))
    .filter((item) => Boolean(item.code && item.name && item.color));
}

function normalizeRegistryPayload(payload: unknown): EnumRegistryData | null {
  if (!isRecord(payload)) return null;

  const roles = normalizeRoles(payload.roles);
  const priorities = normalizePriorities(payload.priorities);
  const workflowStatuses = normalizeWorkflowStatuses(payload.workflowStatuses);

  return {
    roles,
    priorities,
    workflowStatuses,
  };
}

function normalizeRegistryResponse(payload: unknown): EnumRegistryData | null {
  if (!isRecord(payload)) return null;

  if (isRecord(payload.data)) {
    return normalizeRegistryPayload(payload.data);
  }

  return normalizeRegistryPayload(payload);
}

async function fetchEnumRegistryWithCache(): Promise<EnumRegistryData> {
  const now = Date.now();
  if (cachedRegistry && cacheExpiresAtMs > now) {
    return cachedRegistry;
  }

  try {
    const response = await apiClient.get<EnumRegistryResponse>(
      "/config/enum-registry",
    );
    const normalized = normalizeRegistryResponse(response.data);
    if (normalized) {
      cachedRegistry = normalized;
      cacheExpiresAtMs = now + REGISTRY_CACHE_TTL_MS;
      return normalized;
    }
  } catch (error) {
    if (cachedRegistry) {
      return cachedRegistry;
    }
    throw error;
  }

  return cachedRegistry || EMPTY_REGISTRY;
}

export function useEnumRegistryConfig() {
  const { data } = useQuery({
    queryKey: ["config", "enum-registry"],
    queryFn: fetchEnumRegistryWithCache,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => data || cachedRegistry || EMPTY_REGISTRY, [data]);
}

export type PriorityLookup = EnumRegistryPriority;
export type WorkflowStatusLookup = EnumRegistryWorkflowStatus;
export type RoleLookup = EnumRegistryRole;

export function usePriorityLookupConfig() {
  const registry = useEnumRegistryConfig();
  return useMemo(() => registry.priorities, [registry]);
}

export function useWorkflowStatusLookupConfig() {
  const registry = useEnumRegistryConfig();
  return useMemo(() => registry.workflowStatuses, [registry]);
}

export function useRoleLookupConfig() {
  const registry = useEnumRegistryConfig();
  return useMemo(() => registry.roles, [registry]);
}
